import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { Plus, Robot, Download, FilePdf, Key, Eye, EyeSlash, MessengerLogo, WhatsappLogo, Check, Warning, CircleNotch, FileArrowUp, ShoppingBag, Trash, Upload, Link as LinkIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Contactos rápidos de WhatsApp (Programación) — editables
const WaQuickContactsCard = () => {
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/programacion/wa-quick-contacts')
      .then((r) => { setContacts(r.data.contacts || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const update = (i, field, val) => setContacts((prev) => prev.map((c, j) => j === i ? { ...c, [field]: val } : c));
  const remove = (i) => setContacts((prev) => prev.filter((_, j) => j !== i));
  const add = () => setContacts((prev) => [...prev, { name: '', phone: '' }]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/programacion/wa-quick-contacts', { contacts });
      setContacts(r.data.contacts);
      toast.success('✓ Contactos guardados');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando contactos'); }
    setSaving(false);
  };

  return (
    <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-[12px] mb-4" data-testid="wa-quick-contacts-card">
      <div className="flex items-center gap-2 mb-2">
        <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
        <h3 className="text-sm font-bold text-[var(--text)]">Contactos rápidos WhatsApp (envío de reportes)</h3>
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-3">
        Estos aparecen como botones al enviar reportes por WhatsApp desde Programación. Teléfono con 8 dígitos (se agrega 502 automático) o con código de país.
      </p>
      {!loaded ? <CircleNotch size={16} className="animate-spin" /> : (
        <div className="space-y-2">
          {contacts.map((c, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={c.name} onChange={(e) => update(i, 'name', e.target.value)} placeholder="Nombre" className="max-w-[180px]" data-testid={`wa-contact-name-${i}`} />
              <Input value={c.phone} onChange={(e) => update(i, 'phone', e.target.value)} placeholder="50212345678" className="max-w-[170px]" data-testid={`wa-contact-phone-${i}`} />
              <button onClick={() => remove(i)} className="p-1.5 text-red-500 hover:bg-red-500/10 rounded" data-testid={`wa-contact-remove-${i}`}><Trash size={15} /></button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={add} data-testid="wa-contact-add"><Plus size={14} className="mr-1" /> Agregar</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#25D366] hover:bg-[#20B858] text-white" data-testid="wa-contacts-save">
              {saving ? <CircleNotch size={14} className="animate-spin mr-1" /> : <Check size={14} weight="bold" className="mr-1" />} Guardar contactos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Global WA token card — aplica un token de System User a todos los negocios
const GlobalWaTokenCard = () => {
  const [token, setToken] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!token.trim()) { toast.error('Pega el token primero'); return; }
    if (!window.confirm('¿Aplicar este token de WhatsApp a TODOS los negocios con WhatsApp configurado?')) return;
    setSaving(true);
    try {
      const r = await api.put('/businesses/wa-token/bulk', { wa_access_token: token.trim() });
      toast.success(`✓ Token aplicado a ${r.data.updated} negocio(s)${r.data.never_expires ? ' · Token permanente (nunca expira)' : ' · ⚠ OJO: este token SÍ expira'}`);
      setToken('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error aplicando token');
    }
    setSaving(false);
  };

  return (
    <div className="p-4 bg-[#25D366]/5 border border-[#25D366]/30 rounded-[12px] mb-4" data-testid="global-wa-token-card">
      <div className="flex items-center gap-2 mb-2">
        <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
        <h3 className="text-sm font-bold text-[var(--text)]">Token global de WhatsApp (System User)</h3>
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-3">
        Pega aquí el token permanente generado desde Meta Business Suite → Usuarios del sistema → "CRM bot".
        Se validará contra Meta y se aplicará a <strong>todos los negocios</strong> con WhatsApp configurado en un solo paso.
      </p>
      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Input
            type={show ? 'text' : 'password'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="EAA... (token del System User)"
            data-testid="global-wa-token-input"
          />
          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" onClick={() => setShow(!show)}>
            {show ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <Button onClick={apply} disabled={saving || !token.trim()} className="bg-[#25D366] hover:bg-[#20B858] text-white" data-testid="global-wa-token-apply">
          {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Check size={16} weight="bold" className="mr-2" />}
          Aplicar a todos los negocios
        </Button>
      </div>
    </div>
  );
};

// Credential config form for a single business
const BusinessCredentialsForm = ({ business }) => {
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showTokens, setShowTokens] = useState({});
  const [form, setForm] = useState({
    facebook_page_id: '',
    fb_page_access_token: '',
    whatsapp_number: '',
    whatsapp_phone_number_id: '',
    wa_access_token: '',
    wa_business_account_id: '',
  });

  const fetchCreds = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/businesses/${business.id}/credentials`);
      setCreds(res.data);
      setForm({
        facebook_page_id: res.data.facebook_page_id || '',
        fb_page_access_token: '',
        whatsapp_number: res.data.whatsapp_number || '',
        whatsapp_phone_number_id: res.data.whatsapp_phone_number_id || '',
        wa_access_token: '',
        wa_business_account_id: res.data.wa_business_account_id || '',
      });
    } catch {
      toast.error('Error al cargar credenciales');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (expanded) fetchCreds();
  }, [expanded]);

  const handleSave = async (platform) => {
    setSaving(true);
    try {
      const payload = {};
      if (platform === 'messenger') {
        payload.facebook_page_id = form.facebook_page_id;
        if (form.fb_page_access_token) payload.fb_page_access_token = form.fb_page_access_token;
      } else {
        payload.whatsapp_number = form.whatsapp_number;
        payload.whatsapp_phone_number_id = form.whatsapp_phone_number_id;
        payload.wa_business_account_id = form.wa_business_account_id;
        if (form.wa_access_token) payload.wa_access_token = form.wa_access_token;
      }
      await api.put(`/businesses/${business.id}/credentials`, payload);
      toast.success(`Credenciales de ${platform === 'messenger' ? 'Messenger' : 'WhatsApp'} guardadas`);
      fetchCreds();
      setForm(prev => ({
        ...prev,
        fb_page_access_token: '',
        wa_access_token: '',
      }));
    } catch {
      toast.error('Error al guardar credenciales');
    }
    setSaving(false);
  };

  const StatusBadge = ({ active, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
      {active ? <Check size={12} weight="bold" /> : <Warning size={12} weight="bold" />}
      {label}
    </span>
  );

  return (
    <div className="border border-[var(--border)] rounded-[12px] overflow-hidden" data-testid={`business-creds-${business.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg)] transition-colors"
        data-testid={`toggle-creds-${business.id}`}
      >
        <div className="flex items-center gap-3">
          <Key size={20} weight="bold" className="text-[#1D4ED8]" />
          <span className="text-sm font-bold text-[var(--text)]">{business.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {creds && (
            <>
              <StatusBadge active={creds.fb_page_access_token_set} label="Messenger" />
              <StatusBadge active={creds.wa_access_token_set} label="WhatsApp" />
            </>
          )}
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--border)] p-4 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <CircleNotch size={24} className="animate-spin text-[#1D4ED8]" />
            </div>
          ) : (
            <>
              {/* MESSENGER SECTION */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-[var(--border)]">
                  <MessengerLogo size={22} weight="fill" className="text-[#0084FF]" />
                  <h4 className="text-sm font-bold text-[var(--text)]">Facebook Messenger</h4>
                  {creds && <StatusBadge active={creds.fb_page_access_token_set} label={creds.fb_page_access_token_set ? 'Conectado' : 'Sin configurar'} />}
                </div>
                <div>
                  <Label htmlFor={`fb-page-id-${business.id}`}>Page ID de Facebook</Label>
                  <Input
                    id={`fb-page-id-${business.id}`}
                    value={form.facebook_page_id}
                    onChange={(e) => setForm({ ...form, facebook_page_id: e.target.value })}
                    placeholder="Ej: 123456789012345"
                    data-testid={`fb-page-id-${business.id}`}
                  />
                  <p className="text-xs text-[var(--text-faint)] mt-1">El ID numerico de tu pagina de Facebook</p>
                </div>
                <div>
                  <Label htmlFor={`fb-token-${business.id}`}>Page Access Token</Label>
                  <div className="relative">
                    <Input
                      id={`fb-token-${business.id}`}
                      type={showTokens.fb ? 'text' : 'password'}
                      value={form.fb_page_access_token}
                      onChange={(e) => setForm({ ...form, fb_page_access_token: e.target.value })}
                      placeholder={creds?.fb_page_access_token_set ? 'Token configurado (dejar vacio para no cambiar)' : 'EAA...'}
                      data-testid={`fb-token-${business.id}`}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]"
                      onClick={() => setShowTokens(prev => ({ ...prev, fb: !prev.fb }))}
                    >
                      {showTokens.fb ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {creds?.fb_page_access_token_preview && (
                    <p className="text-xs text-[var(--text-faint)] mt-1">Actual: {creds.fb_page_access_token_preview}</p>
                  )}
                </div>
                <Button
                  onClick={() => handleSave('messenger')}
                  disabled={saving || !form.facebook_page_id}
                  className="bg-[#0084FF] hover:bg-[#0073E6]"
                  data-testid={`save-fb-creds-${business.id}`}
                >
                  {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Check size={16} weight="bold" className="mr-2" />}
                  Guardar Messenger
                </Button>
              </div>

              {/* WHATSAPP SECTION */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-[var(--border)]">
                  <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
                  <h4 className="text-sm font-bold text-[var(--text)]">WhatsApp Business</h4>
                  {creds && <StatusBadge active={creds.wa_access_token_set} label={creds.wa_access_token_set ? 'Conectado' : 'Sin configurar'} />}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`wa-phone-${business.id}`}>Numero de WhatsApp</Label>
                    <Input
                      id={`wa-phone-${business.id}`}
                      value={form.whatsapp_number}
                      onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                      placeholder="+52 55 1234 5678"
                      data-testid={`wa-phone-${business.id}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`wa-phone-id-${business.id}`}>Phone Number ID</Label>
                    <Input
                      id={`wa-phone-id-${business.id}`}
                      value={form.whatsapp_phone_number_id}
                      onChange={(e) => setForm({ ...form, whatsapp_phone_number_id: e.target.value })}
                      placeholder="Ej: 123456789012345"
                      data-testid={`wa-phone-id-${business.id}`}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor={`wa-biz-id-${business.id}`}>WhatsApp Business Account ID</Label>
                  <Input
                    id={`wa-biz-id-${business.id}`}
                    value={form.wa_business_account_id}
                    onChange={(e) => setForm({ ...form, wa_business_account_id: e.target.value })}
                    placeholder="Ej: 123456789012345"
                    data-testid={`wa-biz-id-${business.id}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`wa-token-${business.id}`}>Access Token</Label>
                  <div className="relative">
                    <Input
                      id={`wa-token-${business.id}`}
                      type={showTokens.wa ? 'text' : 'password'}
                      value={form.wa_access_token}
                      onChange={(e) => setForm({ ...form, wa_access_token: e.target.value })}
                      placeholder={creds?.wa_access_token_set ? 'Token configurado (dejar vacio para no cambiar)' : 'EAA...'}
                      data-testid={`wa-token-${business.id}`}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]"
                      onClick={() => setShowTokens(prev => ({ ...prev, wa: !prev.wa }))}
                    >
                      {showTokens.wa ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {creds?.wa_access_token_preview && (
                    <p className="text-xs text-[var(--text-faint)] mt-1">Actual: {creds.wa_access_token_preview}</p>
                  )}
                </div>
                <Button
                  onClick={() => handleSave('whatsapp')}
                  disabled={saving || !form.whatsapp_phone_number_id}
                  className="bg-[#25D366] hover:bg-[#20B858] text-[var(--text)]"
                  data-testid={`save-wa-creds-${business.id}`}
                >
                  {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Check size={16} weight="bold" className="mr-2" />}
                  Guardar WhatsApp
                </Button>
              </div>

              {/* SINGLE VENDOR MODE */}
              <SingleVendorSection
                businessId={business.id}
                initialVendorId={creds?.default_vendor_id || null}
                onSaved={fetchCreds}
              />

              {/* CUSTOM WELCOME EXAMPLE */}
              <WelcomeExampleSection
                businessId={business.id}
                initialValue={creds?.welcome_example_product || ''}
                onSaved={fetchCreds}
              />

              {/* FULL BOT KILL SWITCH */}
              <BotKillSwitchSection
                businessId={business.id}
                initialDisabled={!!creds?.bot_fully_disabled}
                onSaved={fetchCreds}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SingleVendorSection = ({ businessId, initialVendorId, onSaved }) => {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState(initialVendorId || '');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setVendorId(initialVendorId || '');
  }, [initialVendorId]);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/vendors', { params: { business_id: businessId, status: 'active' } })
      .then(r => setVendors(r.data || []))
      .catch(() => setVendors([]))
      .finally(() => setLoading(false));
  }, [businessId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/businesses/${businessId}/default-vendor`, { vendor_id: vendorId || null });
      toast.success(res.data.message || 'Configuración guardada');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`single-vendor-section-${businessId}`}>
      <div className="flex items-center gap-2 pb-2 border-b border-[var(--border)]">
        <Robot size={22} weight="fill" className="text-[#1D4ED8]" />
        <h4 className="text-sm font-bold text-[var(--text)]">Modo Vendedor Único</h4>
        {vendorId && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">ACTIVO</span>}
      </div>
      <p className="text-xs text-[var(--text-muted)]">
        Si este negocio lo atiende una sola persona, selecciona el vendedor.
        Toda conversación nueva se le asignará automáticamente y se saltará la pregunta de departamento.
      </p>
      <div>
        <Label>Vendedor asignado</Label>
        <select
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          disabled={loading}
          className="w-full bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1D4ED8]"
          data-testid={`select-default-vendor-${businessId}`}
        >
          <option value="">— Routing por departamentos (por defecto) —</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>
              {v.name} {v.role === 'supervisor' ? '(supervisor)' : ''}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-[var(--text-faint)] mt-1">
          Solo se muestran vendedores ACTIVOS con acceso a este negocio.
        </p>
      </div>
      <Button
        onClick={save}
        disabled={saving || vendorId === (initialVendorId || '')}
        className="bg-[#1D4ED8] hover:bg-[#1E40AF]"
        data-testid={`save-default-vendor-${businessId}`}
      >
        {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Check size={16} weight="bold" className="mr-2" />}
        Guardar
      </Button>
    </div>
  );
};


const WelcomeExampleSection = ({ businessId, initialValue, onSaved }) => {
  const [value, setValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/businesses/${businessId}/welcome-example`, {
        welcome_example_product: value,
      });
      toast.success(res.data.message || 'Ejemplo actualizado');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const preview = (value || '').trim() || 'información sobre sus productos';
  const isDirty = (value || '').trim() !== (initialValue || '').trim();

  return (
    <div className="space-y-3" data-testid={`welcome-example-section-${businessId}`}>
      <div className="flex items-center gap-2 pb-2 border-b border-[var(--border)]">
        <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
        <h4 className="text-sm font-bold text-[var(--text)]">Mensaje de Bienvenida</h4>
        {(initialValue || '').trim() && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            PERSONALIZADO
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--text-muted)]">
        Personaliza el producto/servicio que aparece en el <strong>ejemplo</strong> del mensaje
        automático de bienvenida. Cada negocio puede tener su propio ejemplo (ej: <em>bisagras</em>,
        <em> puertas</em>, <em>herrajes</em>, <em>cocinas</em>).
      </p>
      <div>
        <Label>Producto / servicio de ejemplo</Label>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Ej: bisagras, puertas, ventanas, herrajes..."
          maxLength={80}
          className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)]"
          data-testid={`input-welcome-example-${businessId}`}
        />
        <p className="text-[10px] text-[var(--text-faint)] mt-1">
          Déjalo vacío para usar el texto genérico por defecto. Máx 80 caracteres.
        </p>
      </div>
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg)] p-3">
        <p className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] mb-1">Vista previa del ejemplo</p>
        <p className="text-xs text-[var(--text)] italic">
          Ejemplo: "Soy de Guatemala, busco <span className="text-emerald-400 font-semibold">{preview}</span>"
        </p>
      </div>
      <Button
        onClick={save}
        disabled={saving || !isDirty}
        className="bg-[#25D366] hover:bg-[#20B858] text-[var(--text)]"
        data-testid={`save-welcome-example-${businessId}`}
      >
        {saving ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Check size={16} weight="bold" className="mr-2" />}
        Guardar mensaje
      </Button>
    </div>
  );
};


const BotKillSwitchSection = ({ businessId, initialDisabled, onSaved }) => {
  const [disabled, setDisabled] = useState(!!initialDisabled);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDisabled(!!initialDisabled);
  }, [initialDisabled]);

  const toggle = async (checked) => {
    setSaving(true);
    const prev = disabled;
    setDisabled(checked); // optimistic
    try {
      const res = await api.put(`/businesses/${businessId}/bot-disabled`, {
        bot_fully_disabled: checked,
      });
      toast.success(res.data.message || 'Actualizado');
      onSaved && onSaved();
    } catch (e) {
      setDisabled(prev); // revert
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`bot-kill-switch-section-${businessId}`}>
      <div className="flex items-center gap-2 pb-2 border-b border-[var(--border)]">
        <Robot size={22} weight="fill" className={disabled ? 'text-red-400' : 'text-[var(--text-muted)]'} />
        <h4 className="text-sm font-bold text-[var(--text)]">Apagar Bot Completamente</h4>
        {disabled && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">
            BOT APAGADO
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--text-muted)]">
        Cuando está activo, el bot <strong className="text-[var(--text)]">no envía ningún mensaje</strong> para este negocio:
        ni saludo, ni pregunta de departamento, ni asignación automática. Los mensajes
        de los clientes siguen llegando al Inbox, pero un vendedor humano los debe atender manualmente.
      </p>
      <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)] bg-[var(--bg)]">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[var(--text)]">
            {disabled ? '🔇 Bot apagado para este negocio' : '🤖 Bot activo (saluda y asigna)'}
          </p>
          <p className="text-[10px] text-[var(--text-faint)] mt-0.5">
            Recuerda: hoy el bot solo saluda, asigna y se calla. Aquí lo silencias 100%.
          </p>
        </div>
        <Switch
          checked={disabled}
          onCheckedChange={toggle}
          disabled={saving}
          data-testid={`switch-bot-disabled-${businessId}`}
        />
      </div>
    </div>
  );
};


const McpKeysCard = () => {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/mcp-admin/keys');
      setKeys(res.data.keys || []);
    } catch (e) { /* silencioso */ }
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    if (!newName.trim()) { toast.error('Ponle un nombre a la conexión'); return; }
    try {
      setCreating(true);
      const res = await api.post('/mcp-admin/keys', { name: newName.trim(), profile: 'ADMINISTRADOR' });
      setCreated(res.data);
      setNewName('');
      load();
      toast.success('Credencial MCP creada — cópiala ahora, no se volverá a mostrar');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error creando credencial');
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('¿Revocar esta credencial? ChatGPT dejará de tener acceso de inmediato.')) return;
    try {
      await api.post(`/mcp-admin/keys/${id}/revoke`);
      toast.success('Credencial revocada');
      load();
    } catch (e) { toast.error('Error revocando'); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px] mb-6" data-testid="mcp-keys-card">
      <div className="flex items-center gap-3 mb-2">
        <Robot size={28} weight="bold" className="text-emerald-600" />
        <div>
          <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Conexión MCP — ChatGPT (Solo Lectura)</h2>
          <p className="text-xs text-[var(--text-muted)]">Permite que ChatGPT consulte ventas, metas, inventario y más. Nunca puede crear, editar ni borrar nada.</p>
        </div>
      </div>
      <div className="flex gap-2 mb-4 mt-3">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre (ej: ChatGPT Alfredo — Grupo SACH)" data-testid="mcp-key-name-input" className="max-w-md" />
        <Button onClick={createKey} disabled={creating} data-testid="mcp-key-create-btn">
          {creating ? 'Creando…' : 'Generar credencial'}
        </Button>
      </div>
      {created && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-[12px] mb-4 space-y-2" data-testid="mcp-key-created-box">
          <p className="text-xs font-bold text-emerald-700">⚠️ Copia esto AHORA — no se volverá a mostrar:</p>
          <div className="flex items-center gap-2">
            <code className="text-[11px] bg-[var(--bg)] px-2 py-1 rounded break-all flex-1">{created.mcp_url}</code>
            <Button size="sm" variant="outline" onClick={() => copy(created.mcp_url, 'URL MCP')}>Copiar URL</Button>
          </div>
          <p className="text-[11px] text-[var(--text-muted)]">En ChatGPT: Configuración → Conectores → Modo desarrollador → Crear conector → pega esta URL → Autenticación: "Sin autenticación" (la clave va en la URL).</p>
          <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>Cerrar</Button>
        </div>
      )}
      <div className="space-y-2">
        {keys.map((k) => (
          <div key={k.id} className="flex items-center justify-between p-3 bg-[var(--bg)] border border-[var(--border)] rounded-[10px]" data-testid={`mcp-key-row-${k.id}`}>
            <div>
              <p className="text-sm font-bold text-[var(--text)]">{k.name} {k.revoked_at && <span className="text-red-500 text-[10px]">(REVOCADA)</span>}</p>
              <p className="text-[11px] text-[var(--text-muted)]">{k.key_hint} · {k.profile} · {k.mode} · último uso: {k.last_used_at ? new Date(k.last_used_at).toLocaleString('es-GT') : 'nunca'}</p>
            </div>
            {!k.revoked_at && (
              <Button size="sm" variant="destructive" onClick={() => revoke(k.id)} data-testid={`mcp-key-revoke-${k.id}`}>Revocar</Button>
            )}
          </div>
        ))}
        {keys.length === 0 && <p className="text-xs text-[var(--text-muted)]">Sin credenciales MCP todavía.</p>}
      </div>
    </div>
  );
};

const Settings = () => {
  const { selectedBusiness, fetchBusinesses, user } = useOutletContext();
  const [businesses, setBusinesses] = useState([]);
  const [aiSettings, setAiSettings] = useState(null);
  const [learningStyle, setLearningStyle] = useState(false);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [isBusinessDialogOpen, setIsBusinessDialogOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [catalogUploading, setCatalogUploading] = useState(false);
  const catalogFileRef = useState(null);
  const [businessFormData, setBusinessFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    facebook_page_id: '',
    whatsapp_number: ''
  });

  const isAdmin = user?.role === 'admin';

  const handleDownloadGuide = async (guideType) => {
    try {
      const endpoint = guideType === 'facebook' ? '/download/facebook-guide' : '/download/whatsapp-guide';
      const filename = guideType === 'facebook' 
        ? 'Guia_Facebook_Messenger_API.pdf' 
        : 'Guia_WhatsApp_Business_API.pdf';
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Guia descargada exitosamente');
    } catch {
      toast.error('Error al descargar la guia');
    }
  };

  const handleDownloadCatalogTemplate = async () => {
    try {
      const response = await api.get('/catalog/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_catalogo.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleCatalogUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCatalogUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/catalog/upload/${selectedBusiness.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      fetchProducts();
      fetchAISettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir catalogo');
    }
    setCatalogUploading(false);
    e.target.value = '';
  };

  const fetchProducts = async () => {
    if (!selectedBusiness) return;
    try {
      const res = await api.get(`/catalog/products/${selectedBusiness.id}`);
      setProducts(res.data);
    } catch {
      setProducts([]);
    }
  };

  const handleProductImageUpload = async (productId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/catalog/products/${selectedBusiness.id}/image?product_id=${productId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Imagen actualizada');
      fetchProducts();
    } catch {
      toast.error('Error al subir imagen');
    }
  };

  const catalogUrl = selectedBusiness ? `${BACKEND_URL}/catalogo/${selectedBusiness.id}` : '';

  useEffect(() => {
    fetchAllBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchAISettings();
      fetchProducts();
    }
  }, [selectedBusiness]);

  const fetchAllBusinesses = async () => {
    try {
      const response = await api.get('/businesses');
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const fetchAISettings = async () => {
    try {
      const response = await api.get(`/ai-settings/${selectedBusiness.id}`);
      setAiSettings(response.data);
      const st = await api.get(`/ai-settings/${selectedBusiness.id}/feedback-stats`);
      setFeedbackStats(st.data);
    } catch (error) {
      console.error('Error fetching AI settings:', error);
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/businesses', businessFormData);
      toast.success('Negocio creado');
      setIsBusinessDialogOpen(false);
      resetBusinessForm();
      fetchAllBusinesses();
      fetchBusinesses();
    } catch {
      toast.error('Error al crear negocio');
    }
  };

  const handleAISettingsUpdate = async (updates) => {
    try {
      const response = await api.put(`/ai-settings/${selectedBusiness.id}`, updates);
      setAiSettings(response.data);
      toast.success('Configuracion guardada');
    } catch {
      toast.error('Error al guardar configuracion');
    }
  };

  const resetBusinessForm = () => {
    setBusinessFormData({ name: '', description: '', logo_url: '', facebook_page_id: '', whatsapp_number: '' });
  };

  if (!selectedBusiness) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-[var(--text-muted)]">Selecciona un negocio para continuar</p>
      </div>
    );
  }

  return (
    <div className="h-full p-6 overflow-y-auto" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl tracking-tight font-black text-[var(--text)] mb-2">Configuracion</h1>
        <p className="text-sm text-[var(--text-muted)]">Gestiona tus negocios, APIs y configuracion de IA</p>
      </div>

      {/* MCP ChatGPT — Admin Only */}
      {isAdmin && <McpKeysCard />}

      {/* API Credentials Section - Admin Only */}
      {isAdmin && (
        <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px] mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Key size={28} weight="bold" className="text-[#1D4ED8]" />
            <div>
              <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Credenciales de API por Negocio</h2>
              <p className="text-xs text-[var(--text-muted)]">Configura los tokens de Messenger y WhatsApp para cada negocio</p>
            </div>
          </div>
          <div className="p-3 bg-[#1D4ED8]/5 border border-[#1D4ED8]/20 rounded-[12px] mb-4">
            <p className="text-xs text-[var(--text)] leading-relaxed">
              <strong>Como funciona:</strong> Cada negocio se vincula a una pagina de Facebook y/o numero de WhatsApp. 
              Cuando llegue un mensaje a esa pagina o numero, aparecera automaticamente en el inbox del negocio correspondiente.
            </p>
          </div>
          <GlobalWaTokenCard />
          <WaQuickContactsCard />
          <div className="space-y-3">
            {businesses.map(biz => (
              <BusinessCredentialsForm key={biz.id} business={biz} />
            ))}
          </div>

          {/* Webhook URLs Info */}
          <div className="mt-6 p-4 bg-[var(--bg)] border border-[var(--border)] rounded-[12px] space-y-3">
            <h4 className="text-sm font-bold text-[var(--text)]">URLs de Webhook para Meta Developer Portal</h4>
            <div className="space-y-2">
              <div>
                <p className="text-xs font-semibold text-[#0084FF]">Messenger Webhook:</p>
                <code className="text-xs bg-[var(--surface)] border px-2 py-1 rounded block mt-1 break-all" data-testid="messenger-webhook-url">
                  {BACKEND_URL}/api/webhook/messenger
                </code>
                <p className="text-xs text-[var(--text-faint)] mt-1">Verify Token: <code className="bg-[var(--surface)] border px-1 rounded">social_crm_webhook_verify_2026_secure</code></p>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#25D366]">WhatsApp Webhook:</p>
                <code className="text-xs bg-[var(--surface)] border px-2 py-1 rounded block mt-1 break-all" data-testid="whatsapp-webhook-url">
                  {BACKEND_URL}/api/webhook/whatsapp
                </code>
                <p className="text-xs text-[var(--text-faint)] mt-1">Verify Token: <code className="bg-[var(--surface)] border px-1 rounded">social_crm_wa_webhook_verify_2026_secure</code></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Businesses Section */}
      <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px] mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Mis Negocios</h2>
          {isAdmin && (
            <Button onClick={() => setIsBusinessDialogOpen(true)} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="create-business-btn">
              <Plus size={18} weight="bold" className="mr-2" />
              Nuevo Negocio
            </Button>
          )}
        </div>
        <div className="space-y-3">
          {businesses.map((business) => (
            <div key={business.id} className="p-4 border border-[var(--border)] rounded-[12px]" data-testid={`business-item-${business.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[var(--text)] mb-1">{business.name}</h3>
                  <p className="text-xs text-[var(--text-muted)]">{business.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-[var(--text-muted)]">Moneda:</Label>
                  <select
                    value={business.currency || 'GTQ'}
                    onChange={async (e) => {
                      try {
                        await api.put(`/businesses/${business.id}/currency`, { currency: e.target.value });
                        toast.success('Moneda actualizada');
                        fetchBusinesses();
                      } catch { toast.error('Error'); }
                    }}
                    className="text-xs p-1.5 border border-[var(--border)] rounded-[12px]"
                    data-testid={`currency-select-${business.id}`}
                  >
                    <option value="GTQ">Q - Quetzal</option>
                    <option value="USD">$ - Dolar</option>
                    <option value="EUR">€ - Euro</option>
                    <option value="MXN">$ - Peso MX</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Settings Section */}
      {aiSettings && (
        <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px] mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Robot size={28} weight="bold" className="text-[#1D4ED8]" />
            <div>
              <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Configuracion del Bot IA</h2>
              <p className="text-xs text-[var(--text-muted)]">Personaliza las respuestas automaticas</p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-[var(--bg)] rounded-[12px]">
              <div>
                <Label className="text-sm font-bold text-[var(--text)] mb-1">Bot IA Activado</Label>
                <p className="text-xs text-[var(--text-muted)]">Habilita respuestas automaticas con IA</p>
              </div>
              <Switch checked={aiSettings.enabled} onCheckedChange={(checked) => handleAISettingsUpdate({ enabled: checked })} data-testid="ai-enabled-switch" />
            </div>
            <div className="flex items-center justify-between p-4 bg-[var(--bg)] rounded-[12px]">
              <div>
                <Label className="text-sm font-bold text-[var(--text)] mb-1">Bot solo en horario</Label>
                <p className="text-xs text-[var(--text-muted)]">Activo: Lun-Vie 7:00am-7:00pm y Sáb 7:00am-12:00pm (Guatemala). Fuera de ese horario el bot IA no responde. Apaga este switch para que funcione 24/7.</p>
              </div>
              <Switch checked={aiSettings.horario_activo !== false} onCheckedChange={(checked) => handleAISettingsUpdate({ horario_activo: checked })} data-testid="ai-horario-switch" />
            </div>
            <div className="flex items-center justify-between p-4 bg-[var(--bg)] rounded-[12px]">
              <div>
                <Label className="text-sm font-bold text-[var(--text)] mb-1">Calificación del bot (últimos 30 días)</Label>
                <p className="text-xs text-[var(--text-muted)]">Los clientes califican reaccionando 👍/👎 al mensaje del bot en WhatsApp (o escribiéndolo). Las 👎 se usan para afinar al bot cada semana.</p>
              </div>
              <div className="flex items-center gap-3 shrink-0" data-testid="bot-feedback-stats">
                <span className="text-sm font-black text-emerald-500">👍 {feedbackStats?.up ?? 0}</span>
                <span className="text-sm font-black text-red-400">👎 {feedbackStats?.down ?? 0}</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-[var(--bg)] rounded-[12px]">
              <div>
                <Label className="text-sm font-bold text-[var(--text)] mb-1">Aprender del estilo de los asesores</Label>
                <p className="text-xs text-[var(--text-muted)]">
                  La IA analiza respuestas reales de tus asesores y adopta su tono y técnicas de venta.
                  {aiSettings.estilo_updated_at && ` Última actualización: ${new Date(aiSettings.estilo_updated_at).toLocaleDateString('es-GT')}.`} Se actualiza sola cada domingo.
                </p>
              </div>
              <Button variant="outline" size="sm" disabled={learningStyle} data-testid="ai-learn-style-btn"
                onClick={async () => {
                  setLearningStyle(true);
                  try {
                    await api.post(`/ai-settings/${selectedBusiness.id}/aprender-estilo`);
                    toast.success('El bot aprendió del estilo de tus asesores 🎓');
                    fetchAISettings();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo aprender el estilo'); }
                  finally { setLearningStyle(false); }
                }}>
                {learningStyle ? 'Aprendiendo…' : 'Aprender ahora'}
              </Button>
            </div>
            <div>
              <Label htmlFor="greeting">Mensaje de Saludo</Label>
              <Textarea
                id="greeting"
                value={aiSettings.greeting_message || ''}
                onChange={(e) => setAiSettings({ ...aiSettings, greeting_message: e.target.value })}
                onBlur={() => handleAISettingsUpdate({ greeting_message: aiSettings.greeting_message })}
                rows={2}
                placeholder="Hola! Gracias por contactarnos..."
                data-testid="ai-greeting-input"
              />
            </div>
            <div>
              <Label htmlFor="products">Informacion de Productos</Label>
              <Textarea
                id="products"
                value={aiSettings.product_info || ''}
                onChange={(e) => setAiSettings({ ...aiSettings, product_info: e.target.value })}
                onBlur={() => handleAISettingsUpdate({ product_info: aiSettings.product_info })}
                rows={4}
                placeholder="Describe tus productos o servicios..."
                data-testid="ai-products-input"
              />
            </div>
            <div>
              <Label htmlFor="pricing">Informacion de Precios</Label>
              <Textarea
                id="pricing"
                value={aiSettings.pricing_info || ''}
                onChange={(e) => setAiSettings({ ...aiSettings, pricing_info: e.target.value })}
                onBlur={() => handleAISettingsUpdate({ pricing_info: aiSettings.pricing_info })}
                rows={4}
                placeholder="Lista de precios, promociones, etc..."
                data-testid="ai-pricing-input"
              />
            </div>
            <div>
              <Label htmlFor="hours">Horario de Atencion</Label>
              <Input
                id="hours"
                value={aiSettings.business_hours || ''}
                onChange={(e) => setAiSettings({ ...aiSettings, business_hours: e.target.value })}
                onBlur={() => handleAISettingsUpdate({ business_hours: aiSettings.business_hours })}
                placeholder="Lunes a Viernes 9am-6pm"
                data-testid="ai-hours-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Product Catalog Section - Admin Only */}
      {isAdmin && (
        <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px] mb-6">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingBag size={28} weight="bold" className="text-[#16A34A]" />
            <div>
              <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Catalogo de Productos</h2>
              <p className="text-xs text-[var(--text-muted)]">Sube tu catalogo en Excel para que el bot IA y el catalogo publico lo usen</p>
            </div>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <Button onClick={handleDownloadCatalogTemplate} variant="outline" className="justify-start" data-testid="download-catalog-template-btn">
              <Download size={18} weight="bold" className="mr-2 text-[#1D4ED8]" />
              Descargar Plantilla Excel
            </Button>
            <div className="relative">
              <input
                type="file"
                accept=".xlsx,.csv"
                onChange={handleCatalogUpload}
                className="hidden"
                id="catalog-upload-input"
                data-testid="catalog-upload-input"
              />
              <Button
                onClick={() => document.getElementById('catalog-upload-input').click()}
                disabled={catalogUploading}
                className="w-full justify-start bg-[#16A34A] hover:bg-[#15803D]"
                data-testid="upload-catalog-btn"
              >
                {catalogUploading ? (
                  <CircleNotch size={18} className="animate-spin mr-2" />
                ) : (
                  <FileArrowUp size={18} weight="bold" className="mr-2" />
                )}
                {catalogUploading ? 'Importando...' : 'Subir Catalogo Excel'}
              </Button>
            </div>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => {
                navigator.clipboard.writeText(catalogUrl);
                toast.success('URL del catalogo copiada');
              }}
              data-testid="copy-catalog-url-btn"
            >
              <LinkIcon size={18} weight="bold" className="mr-2 text-[#7C3AED]" />
              Copiar Link Catalogo
            </Button>
          </div>

          {/* Catalog URL */}
          <div className="p-3 bg-[var(--bg)] border border-[var(--border)] rounded-[12px] mb-4">
            <p className="text-xs text-[var(--text-muted)] mb-1">URL publica del catalogo:</p>
            <a href={catalogUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1D4ED8] font-bold break-all hover:underline" data-testid="catalog-public-url">
              {catalogUrl}
            </a>
          </div>

          {/* Products Table */}
          {products.length > 0 ? (
            <div>
              <p className="text-sm font-bold text-[var(--text)] mb-2">{products.length} productos cargados</p>
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-left">
                  <thead className="bg-[var(--bg)] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-xs font-bold text-[var(--text-muted)]">Imagen</th>
                      <th className="px-3 py-2 text-xs font-bold text-[var(--text-muted)]">Producto</th>
                      <th className="px-3 py-2 text-xs font-bold text-[var(--text-muted)]">Categoria</th>
                      <th className="px-3 py-2 text-xs font-bold text-[var(--text-muted)]">Precio</th>
                      <th className="px-3 py-2 text-xs font-bold text-[var(--text-muted)]">Stock</th>
                      <th className="px-3 py-2 text-xs font-bold text-[var(--text-muted)]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id} className="border-t border-[var(--border)] hover:bg-[var(--bg)]" data-testid={`product-row-${p.id}`}>
                        <td className="px-3 py-2">
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <div className="w-10 h-10 bg-[var(--bg)] border border-[var(--border)] rounded flex items-center justify-center">
                              <ShoppingBag size={16} className="text-[#D4D4D8]" />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-xs font-bold text-[var(--text)]">{p.name}</span>
                          {p.description && <p className="text-[10px] text-[var(--text-muted)] truncate max-w-[200px]">{p.description}</p>}
                        </td>
                        <td className="px-3 py-2 text-xs text-[var(--text-muted)]">{p.category || '-'}</td>
                        <td className="px-3 py-2 text-xs font-bold text-[var(--text)]">{p.price > 0 ? `$${p.price.toFixed(2)}` : '-'}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${p.stock === 'No' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                            {p.stock || 'Si'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <label className="cursor-pointer text-xs text-[#1D4ED8] hover:underline font-bold">
                            <Upload size={14} weight="bold" className="inline mr-1" />
                            Imagen
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) handleProductImageUpload(p.id, e.target.files[0]);
                              }}
                            />
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-[var(--bg)] rounded-[12px]">
              <ShoppingBag size={40} weight="thin" className="mx-auto text-[#D4D4D8] mb-2" />
              <p className="text-sm text-[var(--text-muted)]">Aun no hay productos cargados</p>
              <p className="text-xs text-[var(--text-faint)]">Descarga la plantilla, llena tus productos y subela</p>
            </div>
          )}
        </div>
      )}

      {/* Integration Guides Section */}
      <div className="bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[12px]">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-[12px] bg-[#16A34A]/10">
            <FilePdf size={28} weight="bold" className="text-[#16A34A]" />
          </div>
          <div>
            <h2 className="text-lg tracking-tight font-bold text-[var(--text)]">Guias de Integracion</h2>
            <p className="text-xs text-[var(--text-muted)]">Descarga las guias completas para integrar APIs reales</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-[var(--border)] rounded-[12px] hover:border-[#0084FF] transition-colors">
            <div className="flex items-start gap-3 mb-3">
              <MessengerLogo size={24} weight="fill" className="text-[#0084FF] mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-bold text-[var(--text)] mb-1">Facebook Messenger API</h3>
                <p className="text-xs text-[var(--text-muted)] leading-relaxed mb-3">Guia completa para integrar Messenger con webhooks, tokens y codigo Python.</p>
              </div>
            </div>
            <Button onClick={() => handleDownloadGuide('facebook')} className="w-full bg-[#0084FF] hover:bg-[#0073E6]" data-testid="download-facebook-guide-btn">
              <Download size={18} weight="bold" className="mr-2" /> Descargar PDF
            </Button>
          </div>
          <div className="p-4 border border-[var(--border)] rounded-[12px] hover:border-[#25D366] transition-colors">
            <div className="flex items-start gap-3 mb-3">
              <WhatsappLogo size={24} weight="fill" className="text-[#25D366] mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-bold text-[var(--text)] mb-1">WhatsApp Business API</h3>
                <p className="text-xs text-[var(--text-muted)] leading-relaxed mb-3">Guia para WhatsApp Business API Cloud con webhooks y plantillas.</p>
              </div>
            </div>
            <Button onClick={() => handleDownloadGuide('whatsapp')} className="w-full bg-[#25D366] hover:bg-[#20B858]" data-testid="download-whatsapp-guide-btn">
              <Download size={18} weight="bold" className="mr-2" /> Descargar PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Create Business Dialog */}
      <Dialog open={isBusinessDialogOpen} onOpenChange={setIsBusinessDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nuevo Negocio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBusinessSubmit} className="space-y-4" data-testid="business-form">
            <div>
              <Label htmlFor="name">Nombre del Negocio</Label>
              <Input id="name" value={businessFormData.name} onChange={(e) => setBusinessFormData({ ...businessFormData, name: e.target.value })} required data-testid="business-name-input" />
            </div>
            <div>
              <Label htmlFor="description">Descripcion</Label>
              <Textarea id="description" value={businessFormData.description} onChange={(e) => setBusinessFormData({ ...businessFormData, description: e.target.value })} rows={3} data-testid="business-description-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsBusinessDialogOpen(false); resetBusinessForm(); }}>Cancelar</Button>
              <Button type="submit" data-testid="save-business-btn">Crear Negocio</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
